import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, EmptyState, HalalBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { fmtUSD, fmtNum, timeAgo } from "@/lib/format";
import {
  Sprout, ShieldCheck, RefreshCw, Wheat, TrendingUp, Package, MapPin, Clock, Info, CalendarClock,
} from "lucide-react";

const RISK_STYLES = {
  "Low": { bg: "rgba(23,185,120,0.14)", color: "#0E9B63", border: "rgba(23,185,120,0.30)" },
  "Low-Medium": { bg: "rgba(23,185,120,0.12)", color: "#5C7020", border: "rgba(23,185,120,0.26)" },
  "Medium": { bg: "rgba(242,107,33,0.14)", color: "#C0480F", border: "rgba(242,107,33,0.30)" },
  "Medium-High": { bg: "rgba(194,65,58,0.12)", color: "#A5322C", border: "rgba(194,65,58,0.26)" },
};

const RiskBadge = ({ risk }) => {
  const s = RISK_STYLES[risk] || RISK_STYLES["Medium"];
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border"
      style={{ background: s.bg, color: s.color, borderColor: s.border }}>{risk} risk</span>
  );
};

const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  matured: "bg-[color:var(--tharwy-lime)]/15 text-[color:var(--tharwy-lime-700)] border-[color:var(--tharwy-lime)]/30",
  redeemed: "bg-muted text-muted-foreground border-border",
};

function InvestDialog({ plan, onInvested }) {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(plan.min_investment);
  const [busy, setBusy] = useState(false);

  const months = plan.tenor_months;
  const projected = Number(amount) > 0 ? (Number(amount) * plan.rate * (months / 12)) : 0;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/murabaha/invest", { plan_id: plan.id, amount: Number(amount) });
      toast.success("Investment placed", { description: `${fmtUSD(amount)} into ${plan.name}` });
      setOpen(false);
      onInvested();
    } catch (e) {
      toast.error("Could not invest", { description: e?.response?.data?.detail || "Please try again" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full mt-3" data-testid={`murabaha-invest-open-${plan.id}`}>{t("mrb_invest")}</Button>
      </DialogTrigger>
      <DialogContent data-testid="murabaha-invest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sprout className="h-4 w-4 text-emerald-600" /> {plan.name}
          </DialogTitle>
          <DialogDescription>{plan.desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-xs text-muted-foreground">{t("mrb_target_rate")}</div><div className="numeric font-semibold text-emerald-600">{fmtNum(plan.rate * 100)}%</div></div>
            <div><div className="text-xs text-muted-foreground">{t("mrb_tenor")}</div><div className="numeric font-semibold">{months} mo</div></div>
            <div><div className="text-xs text-muted-foreground">{t("mrb_minimum")}</div><div className="numeric font-semibold">{fmtUSD(plan.min_investment, 0)}</div></div>
          </div>
          <div className="space-y-2">
            <Label>{t("mrb_amount_usd")}</Label>
            <Input type="number" min={plan.min_investment} value={amount}
              onChange={(e) => setAmount(e.target.value)} data-testid="murabaha-amount-input" />
          </div>
          <div className="rounded-lg bg-secondary p-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("mrb_projected_profit")}</span>
            <span className="numeric font-semibold text-emerald-600" data-testid="murabaha-projected-profit">{fmtUSD(projected)}</span>
          </div>
          <p className="text-xs text-muted-foreground flex gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Profit is trade-based (cost-plus) and indicative, not guaranteed. Funds are drawn from your USD wallet.</p>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>{t("cancel")}</Button>
          <Button onClick={submit} disabled={busy || Number(amount) < plan.min_investment} data-testid="murabaha-invest-confirm">
            {busy ? "..." : `${t("mrb_invest")} ${fmtUSD(amount, 0)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Murabaha() {
  const { refreshUser, t } = useApp();
  const [plans, setPlans] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [p, pf] = await Promise.all([
        api.get("/murabaha/plans"),
        api.get("/murabaha/portfolio"),
      ]);
      setPlans(p.data.plans);
      setCompliance(p.data.compliance);
      setPortfolio(pf.data);
    } catch (e) {
      toast.error("Failed to load Murabaha data");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // on-demand only, no polling (rate limits)

  const afterAction = async () => { await Promise.all([load(), refreshUser()]); };

  const redeem = async (c) => {
    setBusyId(c.id);
    try {
      const { data } = await api.post("/murabaha/redeem", { contract_id: c.id });
      toast.success("Contract redeemed", {
        description: `${fmtUSD(data.payout)} returned to wallet · profit ${fmtUSD(data.profit)}`,
      });
      await afterAction();
    } catch (e) {
      toast.error("Redeem failed", { description: e?.response?.data?.detail });
    } finally { setBusyId(null); }
  };

  const contracts = portfolio?.contracts || [];

  const daysUntil = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.ceil(ms / 86400000);
  };

  return (
    <AppShell>
      <PageHeader
        title={t("mrb_title")}
        subtitle={t("mrb_subtitle")}
        testid="murabaha-header"
        action={
          <Button variant="secondary" className="gap-2" onClick={load} data-testid="murabaha-refresh">
            <RefreshCw className="h-4 w-4" /> {t("refresh")}
          </Button>
        }
      />

      {/* Compliance banner */}
      {compliance && (
        <Card className="p-5 mb-5 shadow-premium card-premium" data-testid="murabaha-compliance">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-foreground">{compliance.structure}</span>
                <HalalBadge />
                <Badge variant="outline" className="text-xs">{compliance.standard}</Badge>
              </div>
              <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                {compliance.points.map((pt, i) => (
                  <li key={pt} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-emerald-600 mt-0.5">•</span>{pt}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-2 italic">{compliance.disclaimer}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Portfolio KPIs */}
      {portfolio && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <Kpi label={t("mrb_invested_principal")} value={fmtUSD(portfolio.total_principal)} testid="murabaha-kpi-principal" accent />
          <Kpi label={t("mrb_accrued_profit")} value={fmtUSD(portfolio.total_accrued_profit)} testid="murabaha-kpi-profit" />
          <Kpi label={t("mrb_current_value")} value={fmtUSD(portfolio.current_value)} testid="murabaha-kpi-value" />
          <Kpi label={t("mrb_active_contracts")} value={String(portfolio.active_count)} testid="murabaha-kpi-active" />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Plans */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Wheat className="h-4 w-4 text-[color:var(--tharwy-lime-700)]" />
            <h2 className="text-lg font-semibold">{t("mrb_available_flows")}</h2>
          </div>
          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-56 rounded-xl" />)}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {(plans || []).map((p) => (
                <Card key={p.id} className="p-5 shadow-premium flex flex-col" data-testid={`murabaha-plan-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold text-foreground">{p.commodity}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{p.name}</div>
                    </div>
                    <div className="text-end">
                      <div className="text-2xl font-semibold numeric text-emerald-600" data-testid={`murabaha-rate-${p.id}`}>{fmtNum(p.rate * 100)}%</div>
                      <div className="text-[11px] text-muted-foreground">{t("mrb_target_pa")}</div>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{p.desc}</p>
                  <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {p.origin}</div>
                    <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {p.tenor_months} {t("mrb_month_tenor")} · min {fmtUSD(p.min_investment, 0)}</div>
                    {p.spot_price !== undefined && (
                      <div className="flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Spot: {fmtUSD(p.spot_price)}{p.spot_unit ? ` ${p.spot_unit}` : ""}</div>
                    )}
                  </div>
                  <div className="mt-3"><RiskBadge risk={p.risk} /></div>
                  <div className="mt-auto"><InvestDialog plan={p} onInvested={afterAction} /></div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Contracts */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sprout className="h-4 w-4 text-emerald-600" />
            <h2 className="text-lg font-semibold">{t("mrb_your_contracts")}</h2>
          </div>
          <Card className="p-4 shadow-premium">
            {loading ? (
              <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
            ) : contracts.length === 0 ? (
              <EmptyState title={t("mrb_no_contracts")}
                hint={t("mrb_no_contracts_hint")}
                testid="murabaha-empty" />
            ) : (
              <div className="space-y-3" data-testid="murabaha-contracts">
                {contracts.map((c) => {
                  const dLeft = daysUntil(c.maturity_at);
                  const matured = c.status === "matured" || (dLeft !== null && dLeft <= 0);
                  return (
                  <div key={c.id} className="rounded-lg border p-3" data-testid={`murabaha-contract-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm">{c.commodity}</div>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border capitalize ${STATUS_STYLES[c.status] || STATUS_STYLES.active}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-y-1 text-xs">
                      <span className="text-muted-foreground">{t("mrb_principal")}</span>
                      <span className="numeric text-end">{fmtUSD(c.principal)}</span>
                      <span className="text-muted-foreground">{t("mrb_accrued_profit")}</span>
                      <span className="numeric text-end text-emerald-600">{fmtUSD(c.accrued_profit)}</span>
                      <span className="text-muted-foreground">{t("mrb_rate_tenor")}</span>
                      <span className="numeric text-end">{fmtNum(c.rate * 100)}% · {c.tenor_months}mo</span>
                      {c.status === "redeemed" ? (
                        <>
                          <span className="text-muted-foreground">{t("mrb_payout")}</span>
                          <span className="numeric text-end">{fmtUSD(c.payout)}</span>
                        </>
                      ) : (
                        <>
                          <span className="text-muted-foreground">{t("mrb_invested")}</span>
                          <span className="text-end">{timeAgo(c.invested_at)}</span>
                        </>
                      )}
                    </div>
                    {c.status !== "redeemed" && (
                      <div className="mt-2 flex items-center gap-1.5 rounded-md bg-secondary/60 px-2.5 py-1.5" data-testid={`murabaha-countdown-${c.id}`}>
                        <CalendarClock className="h-3.5 w-3.5 text-[color:var(--tharwy-lime-700)]" />
                        {matured ? (
                          <span className="text-xs font-medium text-emerald-600">{t("mrb_matured")}</span>
                        ) : (
                          <span className="text-xs">
                            <span className="text-muted-foreground">{t("mrb_matures_in")} </span>
                            <span className="font-semibold numeric">{dLeft}</span>
                            <span className="text-muted-foreground"> {t("mrb_days")}</span>
                            <span className="text-muted-foreground"> · {new Date(c.maturity_at).toLocaleDateString()}</span>
                          </span>
                        )}
                      </div>
                    )}
                    {c.status !== "redeemed" && (
                      <Button size="sm" variant="secondary" className="w-full mt-2"
                        onClick={() => redeem(c)} disabled={busyId === c.id}
                        data-testid={`murabaha-redeem-${c.id}`}>
                        {busyId === c.id ? "..." : matured ? t("mrb_redeem_matured") : t("mrb_redeem_early")}
                      </Button>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
